import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import useOverwatchAccess from '../../hooks/useOverwatchAccess';
import { API_URL } from '../../utils/api';
import SusScoreBadge from './SusScoreBadge';
import './Overwatch.css';

/**
 * Flag names that have a human-readable description under
 * ``overwatch.flagDesc.<name>``. Module-level so it stays stable; the actual
 * text is resolved per-render via ``t()`` inside the component.
 */
const FLAG_DESC_NAMES = new Set([
  'headshot_anomaly', 'kd_anomaly', 'adr_anomaly', 'opening_kill_dominance',
  'multikill_anomaly', 'duel_dominance', 'snap_aim', 'snap_anomaly',
  'crosshair_precision', 'crosshair_accuracy', 'low_aim_variance', 'smoke_shoot',
  'wallhack_kill', 'fast_reaction', 'prefire', 'weapon_hs_anomaly',
]);

/**
 * Cheat type options for the flag dropdown. ``labelKey`` is resolved via
 * ``t()`` in the component; ``value`` is the API enum.
 */
const CHEAT_TYPES = [
  { value: '', labelKey: 'overwatch.cheatTypeChoose' },
  { value: 'aimbot', labelKey: 'overwatch.cheatTypeAimbot' },
  { value: 'wallhack', labelKey: 'overwatch.cheatTypeWallhack' },
  { value: 'aimbot+wallhack', labelKey: 'overwatch.cheatTypeBoth' },
  { value: 'other', labelKey: 'overwatch.cheatTypeOther' },
];

/**
 * Format a flag's metric data into a readable summary so reviewers don't
 * need to parse raw JSON. ``t`` is passed in since this is a module-level
 * helper (no hook scope of its own).
 */
function formatFlagDetail(name, data, t) {
  if (!data || typeof data !== 'object') return null;

  const parts = [];

  if (data.value !== undefined && data.baseline !== undefined) {
    parts.push(t('overwatch.detailValue', { value: typeof data.value === 'number' ? (data.value * (name.includes('pct') || name.includes('rate') ? 100 : 1)).toFixed(1) : data.value }));
    parts.push(t('overwatch.detailBaseline', { value: typeof data.baseline === 'number' ? (data.baseline * (name.includes('pct') || name.includes('rate') ? 100 : 1)).toFixed(1) : data.baseline }));
    if (data.stddev !== undefined) {
      parts.push(t('overwatch.detailSpread', { value: typeof data.stddev === 'number' ? (data.stddev * (name.includes('pct') || name.includes('rate') ? 100 : 1)).toFixed(2) : data.stddev }));
    }
  }

  if (data.victim_steamid) {
    parts.push(t('overwatch.detailVictim', { name: data.victim_name || data.victim_steamid }));
    parts.push(t('overwatch.detailKillsAgainst', { kills: data.kills_against, total: data.total_encounters }));
  }

  if (data.count !== undefined) {
    parts.push(t('overwatch.detailCount', { count: data.count }));
  }

  return parts.length > 0 ? parts : null;
}

/**
 * Extract round numbers and/or ticks from a flag's data for demo review.
 */
function extractRounds(data) {
  if (!data || typeof data !== 'object') return [];
  if (Array.isArray(data.rounds_contributing)) return data.rounds_contributing;
  if (Array.isArray(data.round_numbers)) return data.round_numbers;
  if (Array.isArray(data.evidence_ticks)) {
    return data.evidence_ticks.map((t) => ({
      round: t.round,
      tick: t.tick,
      detail: t.detail || null,
    }));
  }
  if (Array.isArray(data.examples)) {
    return data.examples.map((e) => ({
      tick: e.tick,
      round: e.round || null,
      detail: e.detail || null,
    }));
  }
  return [];
}

/**
 * Detailed view of a single suspicion report. Shows all metrics that fed the
 * score plus review actions (clear / flag). Provides direct links to the
 * matching match page and the player's overwatch profile so reviewers can
 * jump between related context quickly.
 */
export default function OverwatchReport() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { reportId } = useParams();
  const { hasAccess, canManage, loading: accessLoading } = useOverwatchAccess();
  const { getAuthHeaders } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [cheatType, setCheatType] = useState('');
  const [overwatchers, setOverwatchers] = useState([]);
  const [selectedOw, setSelectedOw] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, owRes] = await Promise.all([
        fetch(`${API_URL}/api/overwatch/reports/${reportId}`, {
          headers: getAuthHeaders(),
        }),
        fetch(`${API_URL}/api/overwatch/overwatchers`, {
          headers: getAuthHeaders(),
        }),
      ]);
      if (res.ok) {
        const data = await res.json();
        setReport(data);
        setNote(data.review_note || '');
      }
      if (owRes.ok) {
        const owData = await owRes.json();
        setOverwatchers(owData.results || []);
      }
    } finally {
      setLoading(false);
    }
  }, [reportId, getAuthHeaders]);

  useEffect(() => {
    if (!accessLoading && !hasAccess) {
      navigate('/', { replace: true });
      return;
    }
    if (hasAccess) load();
  }, [accessLoading, hasAccess, navigate, load]);

  const review = async (newStatus) => {
    try {
      const body = { status: newStatus, note };
      if (newStatus === 'flagged' && cheatType) {
        body.cheat_type = cheatType;
      }
      const res = await fetch(`${API_URL}/api/overwatch/reports/${reportId}/review`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setReport(await res.json());
      }
    } catch (err) {
      console.error('Failed to review report', err);
    }
  };

  const assignOw = async (owId) => {
    try {
      const res = await fetch(`${API_URL}/api/overwatch/reports/${reportId}/assign`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ overwatcher_ids: [owId] }),
      });
      if (res.ok) {
        const data = await res.json();
        setReport((prev) => ({ ...prev, assignments: data.assignments }));
        setSelectedOw('');
      }
    } catch (err) {
      console.error('Failed to assign', err);
    }
  };

  const unassignOw = async (assignmentId) => {
    try {
      const res = await fetch(
        `${API_URL}/api/overwatch/reports/${reportId}/assignments/${assignmentId}`,
        { method: 'DELETE', headers: getAuthHeaders() },
      );
      if (res.ok) {
        setReport((prev) => ({
          ...prev,
          assignments: (prev.assignments || []).filter((a) => a.id !== assignmentId),
        }));
      }
    } catch (err) {
      console.error('Failed to unassign', err);
    }
  };

  if (accessLoading || !hasAccess || loading) return null;
  if (!report) return <div className="overwatch-empty">{t('overwatch.reportNotFound')}</div>;

  const metrics = report.metrics || {};
  const summary = metrics.summary || {};
  const demoAnalyzed = metrics.demo_analyzed === true;
  const META_KEYS = new Set([
    'summary',
    'baseline_available',
    'baseline_matches_analyzed',
    'stddev_flags_enabled',
    'demo_analyzed',
    'stats_raw_score',
    'demo_raw_score',
    'demo_kills_analyzed',
  ]);
  const flagEntries = Object.entries(metrics).filter(([key]) => !META_KEYS.has(key));

  return (
    <div className="overwatch-page">
      <button className="overwatch-back-btn" onClick={() => navigate(-1)}>
        ← {t('profile.back')}
      </button>

      <div className="overwatch-report-detail">
        <h2>
          <SusScoreBadge score={report.sus_score} /> {report.player_name || report.steam_id}
        </h2>

        <div className={`overwatch-demo-badge ${demoAnalyzed ? 'on' : 'off'}`}>
          {demoAnalyzed
            ? `✓ ${t('overwatch.demoDone', { kills: metrics.demo_kills_analyzed || 0, flagged: metrics.demo_raw_score ?? 0 })}`
            : `⚠ ${t('overwatch.demoNone')}`}
        </div>

        {(report.prior_cleared > 0 || report.prior_flagged > 0) && (
          <div className={`overwatch-history-hint ${report.prior_flagged > 0 ? 'warn' : 'info'}`}>
            {report.prior_flagged > 0 ? '⚠️ ' : 'ℹ️ '}
            {t('overwatch.priorCleared', { count: report.prior_cleared })}
            {report.prior_flagged > 0 && t('overwatch.priorFlaggedSuffix', { count: report.prior_flagged })}
            .
            {report.prior_flagged === 0 && report.prior_cleared >= 3 &&
              ` ${t('overwatch.priorNaturallySkilled')}`}
            {report.prior_flagged > 0 &&
              ` ${t('overwatch.priorTakeSeriously')}`}
          </div>
        )}

        <div className="overwatch-report-meta">
          <div>
            <div className="label">{t('nav.match')}</div>
            <div className="value">
              <a className="overwatch-link" href={`/stats/matches/${report.match_id}`}>
                #{report.match_id}
              </a>
            </div>
          </div>
          <div>
            <div className="label">{t('overwatch.colMap')}</div>
            <div className="value">
              {report.map_name || t('overwatch.mapFallback', { n: report.map_number })}
            </div>
          </div>
          {report.demo_url && (
            <div>
              <div className="label">Demo</div>
              <div className="value">
                <a
                  className="overwatch-demo-link"
                  href={report.demo_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ⬇ {t('overwatch.downloadDemo')}
                </a>
              </div>
            </div>
          )}
          {report.demo_url && (
            <div className="overwatch-demo-guide-col">
              <div className="label">{t('overwatch.reviewDemo')}</div>
              <div className="value overwatch-text-small">
                {t('overwatch.demoGuide1')} <code>Steam\steamapps\common\Counter-Strike Global Offensive\game\csgo</code>
                <br />{t('overwatch.demoGuide2')} <code>playdemo filnamn.dem</code> {t('overwatch.demoGuide2b')} <code>demo_gototick TICK</code>
                <br />{t('overwatch.demoGuide3')}
              </div>
            </div>
          )}
          <div>
            <div className="label">Steam ID</div>
            <div className="value overwatch-text-small">{report.steam_id}</div>
          </div>
          <div>
            <div className="label">{t('overwatch.labelReview')}</div>
            <div className="value">
              <span className={`review-status ${report.review_status}`}>
                {report.review_status}
              </span>
            </div>
          </div>
          <div>
            <div className="label">{t('overwatch.colAnalyzed')}</div>
            <div className="value overwatch-text-small">
              {report.analyzed_at ? new Date(report.analyzed_at).toLocaleString('sv-SE') : '—'}
            </div>
          </div>
          {report.reviewed_by && (
            <div>
              <div className="label">{t('overwatch.reviewedBy')}</div>
              <div className="value">{report.reviewed_by}</div>
            </div>
          )}
        </div>

        <h3>{t('overwatch.matchSummary')}</h3>
        <div className="overwatch-report-meta">
          <div><div className="label">K/D/A</div><div className="value">{summary.kills}/{summary.deaths}/{summary.assists || '—'}</div></div>
          <div><div className="label">{t('overwatch.rounds')}</div><div className="value">{summary.rounds}</div></div>
          <div><div className="label">HS%</div><div className="value">{((summary.headshot_pct || 0) * 100).toFixed(0)}%</div></div>
          <div><div className="label">ADR</div><div className="value">{summary.adr}</div></div>
          <div><div className="label">{t('overwatch.openingKills')}</div><div className="value">{summary.opening_kills}</div></div>
          <div><div className="label">{t('overwatch.multikills')}</div><div className="value">{summary.multikills}</div></div>
        </div>

        <h3 className="overwatch-section-heading">
          {t('overwatch.flagsHeading', { count: flagEntries.length })}
          <span className="overwatch-section-subtitle">
            {t('overwatch.flagsSubtitle')}
            {demoAnalyzed
              ? ` ${t('overwatch.flagsSubtitleDemo')}`
              : ` ${t('overwatch.flagsSubtitleStats')}`}
          </span>
        </h3>
        {flagEntries.length === 0 ? (
          <div className="overwatch-empty overwatch-empty-compact">{t('overwatch.noFlagsTriggered')}</div>
        ) : (
          flagEntries.map(([name, data]) => {
            const description = FLAG_DESC_NAMES.has(name) ? t(`overwatch.flagDesc.${name}`) : null;
            const details = formatFlagDetail(name, data, t);
            const rounds = extractRounds(data);

            return (
              <div key={name} className="overwatch-flag-card">
                <h4>{name}</h4>
                {description && (
                  <p className="overwatch-flag-description">{description}</p>
                )}
                {details && (
                  <div className="overwatch-flag-details">
                    {details.map((d, i) => <span key={i}>{d}</span>)}
                  </div>
                )}
                {rounds.length > 0 && (
                  <div className="overwatch-flag-rounds">
                    <span className="overwatch-flag-rounds-label">
                      {t('overwatch.reviewInDemo')}
                    </span>
                    {rounds.map((r, i) => {
                      const tick = typeof r === 'object' ? r.tick : null;
                      const round = typeof r === 'object' ? r.round : r;
                      const detail = typeof r === 'object' ? r.detail : null;
                      let label = tick
                        ? t('overwatch.tickLabel', { tick }) + (round ? ` (${t('overwatch.roundLower', { round })})` : '')
                        : t('overwatch.roundLabel', { round });
                      if (detail) label += ` — ${detail}`;
                      const cmd = tick ? `demo_gototick ${tick}` : null;

                      return (
                        <span
                          key={i}
                          className={`overwatch-round-chip ${cmd ? 'copyable' : ''}`}
                          title={cmd ? t('overwatch.clickToCopy', { cmd }) : undefined}
                          onClick={cmd ? () => navigator.clipboard.writeText(cmd) : undefined}
                        >
                          {label}
                          {cmd && <code className="overwatch-tick-cmd">{cmd}</code>}
                        </span>
                      );
                    })}
                    <p className="overwatch-flag-tip">
                      {t('overwatch.roundsTip')}
                    </p>
                  </div>
                )}
                {!details && rounds.length === 0 && (
                  <pre>{JSON.stringify(data, null, 2)}</pre>
                )}
              </div>
            );
          })
        )}

        <h3 className="overwatch-section-heading">
          {t('overwatch.assignedReviewers')}
          <span className="overwatch-section-subtitle">
            {t('overwatch.assignedReviewersSub')}
          </span>
        </h3>
        <div className="overwatch-assignments">
          {(report.assignments || []).length === 0 ? (
            <div className="overwatch-muted-text">{t('overwatch.noOneAssignedYet')}</div>
          ) : (
            <div className="overwatch-assignment-list">
              {(report.assignments || []).map((a) => (
                <div key={a.id} className="overwatch-assignment-chip">
                  <span className={`review-status ${a.status}`}>{a.status}</span>
                  <span className="overwatch-assignment-name">{a.display_name}</span>
                  {a.reviewed_at && (
                    <span className="overwatch-muted-text">
                      {new Date(a.reviewed_at).toLocaleDateString('sv-SE')}
                    </span>
                  )}
                  {canManage && (
                    <button
                      className="overwatch-assignment-remove"
                      onClick={() => unassignOw(a.id)}
                      title={t('overwatch.removeAssignment')}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {canManage && (
            <div className="overwatch-assign-row">
              <select
                className="overwatch-cheat-type-select"
                value={selectedOw}
                onChange={(e) => setSelectedOw(e.target.value)}
              >
                <option value="">{t('overwatch.chooseOverwatcher')}</option>
                {overwatchers
                  .filter((ow) => !(report.assignments || []).some((a) => a.overwatcher_id === ow.id))
                  .map((ow) => (
                    <option key={ow.id} value={ow.id}>{ow.display_name}</option>
                  ))}
              </select>
              <button
                className="overwatch-refresh-btn"
                onClick={() => selectedOw && assignOw(selectedOw)}
                disabled={!selectedOw}
              >
                + {t('overwatch.assign')}
              </button>
            </div>
          )}
        </div>

        <h3>{t('overwatch.assessment')}</h3>
        <textarea
          className="overwatch-review-textarea"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t('overwatch.decisionPlaceholder')}
        />

        <div className="overwatch-actions">
          <button className="clear-btn" onClick={() => review('cleared')}>
            ✓ {t('overwatch.clearLegit')}
          </button>
          <div className="overwatch-flag-action">
            <select
              className="overwatch-cheat-type-select"
              value={cheatType}
              onChange={(e) => setCheatType(e.target.value)}
            >
              {CHEAT_TYPES.map((ct) => (
                <option key={ct.value} value={ct.value}>{t(ct.labelKey)}</option>
              ))}
            </select>
            <button className="flag-btn" onClick={() => review('flagged')}>
              ⚠ {t('overwatch.flagAsCheat')}
            </button>
          </div>
          <button onClick={() => review('pending')}>↺ {t('overwatch.resetToPending')}</button>
        </div>

        {report.aggregate && report.aggregate.matches_analyzed > 0 && (
          <div className="overwatch-aggregate-section">
            <h3 className="overwatch-section-heading">
              {t('overwatch.playerHistory')}
              <span className="overwatch-section-subtitle">
                {t('overwatch.avgScoreBasedOn', { count: report.aggregate.matches_analyzed })}
              </span>
            </h3>
            <div className="overwatch-report-meta">
              <div>
                <div className="label">{t('overwatch.avgScore')}</div>
                <div className="value">
                  <SusScoreBadge score={report.aggregate.rolling_avg_sus_score} />
                </div>
              </div>
              <div>
                <div className="label">{t('overwatch.colCommonFlags')}</div>
                <div className="value">
                  <div className="flag-chips">
                    {Object.entries(report.aggregate.flag_totals || {})
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 5)
                      .map(([f, c]) => <span key={f} className="flag-chip">{f} ×{c}</span>)}
                  </div>
                </div>
              </div>
            </div>
            <button
              className="overwatch-profile-btn"
              onClick={() => navigate(`/overwatch/player/${report.steam_id}`)}
            >
              {t('overwatch.viewFullProfile')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
